import { t } from 'ttag'
import isEmail from 'validator/lib/isEmail'
import { useMutation } from 'react-query'
import { useDispatch } from 'react-redux'
import { useContext, useMemo, useCallback } from 'react'

import { Text } from '@h2/components/typography/text'
import { api, errors } from '@h2/api'
import { Globals } from '@h2/globals'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { Button } from '@h2/components/buttons/button'
import { useForm } from '@h2/common/components/hooks/useForm'
import { setUser } from '@h2/common/actions/setUser'
import { Actions } from '@h2/common/components/user/components'
import { primaryTypes } from '@h2/common/components/user/utils'
import { Page } from '@h2/common/components/page'
import { Alert } from '@h2/components/display/alert'
import { showUserFeedback } from '@h2/common/actions/showUserFeedback'

const Email = () => {
  const { user } = useContext(Globals)
  const dispatch = useDispatch()
  const reduxDispatch = useDispatch()
  const usernameIsEmail = useMemo(() => user?.userName === user?.email, [user])
  const canEditEmail = useMemo(
    () => !user?.email || (!user?.hasManager && !user?.educatorAccountGranted),
    [user]
  )
  const {
    register,
    handleOnSubmit,
    setValidation,
    error,
    isInvalid,
    required,
    value: formValues
  } = useForm({
    validators: {
      async email(form, checkAvailability = false) {
        const emailEntered = form.email?.trim()
        const isStudentWithUsername =
          form.primaryType?.value === primaryTypes.student.id &&
          user.userName &&
          !usernameIsEmail

        if (emailEntered) {
          if (!isEmail(emailEntered)) {
            return t`Invalid Email address`
          }

          if (usernameIsEmail) {
            if (/^___/.test(emailEntered)) {
              return t`Invalid format, Email can not start with "___"`
            }

            if (checkAvailability) {
              let isAvailable = false

              try {
                isAvailable = await api.fetch('/auth/usernameAvailable', {
                  method: 'POST',
                  body: JSON.stringify(emailEntered)
                })
              } catch {
                return t`An error occurred while checking availability`
              }

              if (!isAvailable) {
                return t`Already in use`
              }
            }
          }
        } else if (usernameIsEmail || !isStudentWithUsername) {
          return t`Email is required`
        }
      }
    },
    initialValues: {
      email: user?.email
    }
  })
  const { mutate, isLoading } = useMutation(
    body => {
      return api.fetch(`/personalProfile/me`, {
        method: 'POST',
        body: JSON.stringify({
          firstName: user.firstName,
          lastName: user.lastName,
          graduationYear: user.graduationYear,
          languageId: user.languageId,
          primaryType: user.primaryType,
          school: user.school,
          studentType: user.studentType,
          state: user.state,
          ...body
        })
      })
    },
    {
      onSuccess: useCallback(
        updatedUser => {
          dispatch(setUser(updatedUser))

          window.location.replace('/help/redirect')
        },
        [dispatch]
      ),
      onError: useCallback(
        err => {
          const validation = errors.getValidation(err)

          if (validation?.email) {
            setValidation(validation)
          } else {
            reduxDispatch(
              showUserFeedback(
                t`Could not update email address, please try again later`,
                'error'
              )
            )
          }
        },
        [setValidation, reduxDispatch]
      )
    }
  )
  const onSubmit = useCallback(
    form => {
      // Changes may have made some form values obsolete.
      const obsolete = Object.keys(required).filter(field => !required[field])

      // Remove obsolete, and empty fields
      Object.keys(form).forEach(field => {
        if (obsolete.includes(field) || Number.isNaN(form[field]) || !form[field]) {
          delete form[field]
        }
      })

      mutate(form)
    },
    [required, mutate]
  )

  if (!canEditEmail) {
    return null
  }

  return (
    <Page>
      <Page.Title>{t`Update Email`}</Page.Title>
      <Page.Layout>
        <Page.Content>
          <Alert
            type="info"
            message={t`A valid e-mail address is required before you can access the PowerSchool community help system.`}
          />
          <Text my="25px" size="large">
            {t`Use the form to update the email address associated with your account.`}
          </Text>
          <form onSubmit={handleOnSubmit(onSubmit)} noValidate>
            <Field
              isRequired={true}
              label={t`Email`}
              desc={canEditEmail ? error.email : t`Validated emails cannot be changed`}
              status={error.email ? 'error' : 'default'}>
              <TextInput
                isDisabled={!canEditEmail}
                {...register('email', {
                  required: true,
                  runAsyncCheck: user?.email !== formValues.email
                })}
              />
            </Field>
            <Actions>
              <Button
                type="submit"
                size="large"
                align="center"
                isLoading={isLoading}
                isDisabled={isInvalid || isLoading}>
                {t`Update Email and Continue`}
              </Button>
            </Actions>
          </form>
        </Page.Content>
      </Page.Layout>
    </Page>
  )
}

export { Email }
